import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    Search, Scale, Loader2, Sparkles, FileBox, ShieldAlert, Gavel,
    Lightbulb, TrendingUp, FileText, History, Clock, GitMerge, ExternalLink,
    MessageSquare, Send, Bot, User, CornerDownLeft, Trash2, X,
    ChevronDown, ChevronUp, Check, BookOpen
} from "lucide-react";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { marked } from "marked";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { useDespachos } from "@/hooks/useDespachos";
import { createAuthHeaders } from "@/lib/auth-utils";
import { supabase } from "@/lib/supabase";
import { UsageTracker } from "@/lib/services/usage-tracker";
import { getAgentBySlug } from "@/lib/agents";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

// Configure marked with the newer .use() API
marked.use({ breaks: true, gfm: true });

// ─── Types ───────────────────────────────────────────────────────────────────

interface ResearchResult {
    status?: "processing" | "completed" | "failed" | "rejected";
    error?: string;
    message?: string;
    guard_rail_details?: { reason?: string; };
    executive_summary?: string;
    sintesis_juridica?: {
        problema_juridico: string;
        regla_juridica: string;
        excepcion: string;
        conclusion: string;
    };
    confidence_score?: number;
    jurisprudence_trend?: {
        dominante: number;
        excepcion: number;
        trend_summary: string;
        tension_points?: Array<{
            case_a: string;
            case_b: string;
            conflict: string;
        }>;
    };
    suggested_questions?: string[];
    legal_concepts?: Array<string | { term: string; definition: string }>;
    cases?: Array<{
        title: string;
        court: string;
        date: string;
        summary: string;
        ratio_decidendi?: string;
        url: string;
        extract?: string;
        authority_level?: string;
        relevance_score?: number;
        relevance_explanation?: string;
        suggested_document_type?: string;
    }>;
    legislation?: Array<{
        title: string;
        summary: string;
        url: string;
        relevance_score?: number;
        relevance_explanation?: string;
        suggested_document_type?: string;
    }>;
    pdf_summary_url?: string;
    summary?: string;
    search_id?: string;
}

interface ResearchForm {
    query: string;
    jurisdiction: string;
    specific_areas: string[];
    case_id?: string;
    skill_id?: string;
}

interface Skill {
    id: string;
    name: string;
    agent_type: string;
    description: string;
}

interface HistoryItem {
    id: string;
    title?: string;
    query?: string;
    content?: string;
    metadata?: any;
    created_at?: string;
}

const normalizeHistoryItem = (raw: any): HistoryItem => {
    const metadata = typeof raw?.metadata === "string"
        ? (() => {
            try {
                return JSON.parse(raw.metadata);
            } catch {
                return {};
            }
        })()
        : (raw?.metadata || {});

    return {
        ...raw,
        metadata,
    };
};

const JURISDICTIONS = [
    { value: "Constitucional", label: "Constitucional (Corte Constitucional)" },
    { value: "Ordinaria", label: "Ordinaria (Corte Suprema de Justicia)" },
    { value: "Contencioso Administrativa", label: "Contencioso Administrativa (Consejo de Estado)" },
    { value: "Disclipinaria", label: "Disciplinaria (C.N. Disciplina)" },
];

export default function LegalResearchV2() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const apiClient = useApiClient();
    const { processes } = useDespachos();
    const { startResearchRun, getRun, cancelRun } = useAgentExecution();
    const { openDocument } = useDocumentViewer();
    const isMobile = useIsMobile();
    const [mobileWorkspaceTab, setMobileWorkspaceTab] = useState<"analysis" | "sources" | "chat">("analysis");
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [step, setStep] = useState(1);
    const [form, setForm] = useState<ResearchForm>({
        query: "",
        jurisdiction: "Constitucional",
        specific_areas: [],
        case_id: "",
        skill_id: "none"
    });

    const [skills, setSkills] = useState<Skill[]>([]);
    const [loadingSkills, setLoadingSkills] = useState(false);
    const [activeSkillDetails, setActiveSkillDetails] = useState<any>(null);
    const [loadingSkillDetails, setLoadingSkillDetails] = useState(false);
    const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);

    useEffect(() => {
        const fetchSkills = async () => {
            setLoadingSkills(true);
            try {
                const authHeaders = await createAuthHeaders();
                const res = await fetch(`${apiClient.baseUrl}/api/v1/contract-review/skills`, {
                    headers: authHeaders
                });
                if (res.ok) {
                    const data = await res.json();
                    // Map and detect agent_type from description prefix if missing
                    const mappedSkills = (data.skills || []).map((s: any) => {
                        let agentType = s.agent_type;
                        if (!agentType && s.description) {
                            if (s.description.includes("[LEGAL_RESEARCH]")) agentType = "legal_research";
                            else if (s.description.includes("[DOCUMENT_DRAFTING]")) agentType = "document_drafting";
                            else if (s.description.includes("[CONTRACT_REVIEW]")) agentType = "contract_review";
                        }
                        return { ...s, agent_type: agentType };
                    });

                    // Filter for research skills
                    const researchSkills = mappedSkills.filter((s: Skill) =>
                        s.agent_type === 'legal_research' || !s.agent_type
                    );
                    setSkills(researchSkills);
                }
            } catch (error) {
                console.error("Error fetching skills:", error);
            } finally {
                setLoadingSkills(false);
            }
        };
        fetchSkills();
    }, [apiClient.baseUrl]);

    // Fetch Skill details when selected
    useEffect(() => {
        const fetchSkillDetails = async () => {
            if (!form.skill_id || form.skill_id === "none") {
                setActiveSkillDetails(null);
                return;
            }

            setLoadingSkillDetails(true);
            try {
                const authHeaders = await createAuthHeaders();
                const res = await fetch(`${apiClient.baseUrl}/api/v1/contract-review/skills/${form.skill_id}`, {
                    headers: authHeaders
                });
                if (res.ok) {
                    const data = await res.json();
                    setActiveSkillDetails(data.skill);
                }
            } catch (error) {
                console.error("Error fetching skill details:", error);
            } finally {
                setLoadingSkillDetails(false);
            }
        };
        fetchSkillDetails();
    }, [form.skill_id, apiClient.baseUrl]);

    // --- AUTO-EXECUTION FROM CHATBOT ---
    useEffect(() => {
        const prefill = (location.state as any)?.prefill;
        if (prefill && prefill.research_topic) {
            // 1. Update form
            setForm({
                query: prefill.research_topic,
                jurisdiction: prefill.jurisdiction || "Constitucional",
                specific_areas: []
            });
            // 2. Set UI to results mode
            setStep(3);
            // 3. Trigger research (short delay to ensure state is set)
            setTimeout(() => {
                handleInvestigate(prefill.research_topic, true);
            }, 100);

            // Clean up state to prevent re-execution on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    const [queryPath, setQueryPath] = useState<Array<{ query: string; timestamp: number; result?: ResearchResult }>>([]);

    const [showSources, setShowSources] = useState(true);
    const [showInsights, setShowInsights] = useState(true);

    const [showHistory, setShowHistory] = useState(false);
    const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [openedFromExpediente, setOpenedFromExpediente] = useState(false);

    // ==========================================
    // Chat & Threading
    // ==========================================
    const [chatMessages, setChatMessages] = useState<Array<{ role: "user" | "assistant", content: string, timestamp: string }>>([]);
    const [chatInput, setChatInput] = useState("");
    const [activeTab, setActiveTab] = useState("insights");
    const chatEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (activeTab === "chat") {
            scrollToBottom();
        }
    }, [chatMessages, activeTab]);

    const handleDeleteHistory = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!window.confirm("¿Estás seguro de que deseas eliminar esta investigación? Esta acción no se puede deshacer.")) {
            return;
        }

        const toastId = toast.loading("Eliminando investigación...");
        try {
            const authHeaders = await createAuthHeaders();
            const res = await fetch(`${apiClient.baseUrl}/api/v1/dashboard/legal-research/documents/${id}`, {
                method: "DELETE",
                headers: { ...authHeaders }
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.detail || "Error al eliminar");
            }

            toast.success("Investigación eliminada", { id: toastId });
            setHistoryItems(prev => prev.filter(item => item.id !== id));
        } catch (error: any) {
            console.error("Error deleting history:", error);
            toast.error(error.message || "Error al eliminar la investigación", { id: toastId });
        }
    };

    // ==========================================
    // Thread Tracking
    // ==========================================
    const [currentSearchId, setCurrentSearchId] = useState<string | null>(null);
    const [rootSearchId, setRootSearchId] = useState<string | null>(null);

    useEffect(() => {
        if (showHistory) {
            const loadHistory = async () => {
                setLoadingHistory(true);
                try {
                    const authHeaders = await createAuthHeaders();
                    const res = await fetch(`${apiClient.baseUrl}/api/v1/dashboard/legal-research/documents`, {
                        headers: { ...authHeaders }
                    });
                    if (!res.ok) throw new Error("Failed to fetch history");
                    const data = await res.json();
                    setHistoryItems(data.documents || []);
                } catch (error) {
                    console.error("Error loading history:", error);
                    toast.error("Error al cargar el historial");
                } finally {
                    setLoadingHistory(false);
                }
            };
            loadHistory();
        }
    }, [showHistory, apiClient.baseUrl]);

    const loadResearchHistoryItem = useCallback((rawItem: HistoryItem, closeHistory = true) => {
        const item = normalizeHistoryItem(rawItem);
        const initialQuery = item.query || item.metadata?.query || item.metadata?.research_topic || item.title || "Investigación Histórica";
        setForm(f => ({ ...f, query: initialQuery }));
        const baseResult: ResearchResult = {
            ...item.metadata,
            cases: item.metadata?.cases || [],
            legislation: item.metadata?.legislation || [],
            summary: item.metadata?.summary || item.metadata?.executive_summary || item.content,
            search_id: item.id,
            status: "completed"
        };
        if (item.metadata?.query_path?.length) {
            const formattedPath = item.metadata.query_path.map((step: any) => ({
                query: typeof step === "string" ? step : (step.query || step.research_topic || "Refinamiento"),
                timestamp: new Date(
                    typeof step === "string" ? Date.now() : (step.timestamp || Date.now())
                ).getTime(),
                result: typeof step === "string"
                    ? { ...baseResult, status: "completed" as const }
                    : { ...baseResult, ...step, status: "completed" as const }
            }));
            setQueryPath(formattedPath);
            const lastStep = formattedPath[formattedPath.length - 1];
            setLocalResult({ ...baseResult, ...lastStep.result, cases: baseResult.cases, legislation: baseResult.legislation });
        } else {
            const singleStep = {
                query: item.metadata?.query || item.metadata?.research_topic || item.title || "Investigación Histórica",
                status: "completed" as const,
                timestamp: new Date(item.created_at || Date.now()).getTime(),
                result: { ...baseResult, status: "completed" as const }
            };
            setQueryPath([singleStep]);
            setLocalResult(singleStep.result);
        }
        setCurrentSearchId(item.id);
        setRootSearchId(item.id);
        setStep(3);
        if (closeHistory) setShowHistory(false);
    }, []);

    useEffect(() => {
        const historyItemId = (location.state as any)?.historyItemId;
        const linkedArtifact = (location.state as any)?.linkedArtifact;
        if (!historyItemId && !linkedArtifact) return;
        if ((location.state as any)?.fromExpediente) {
            setOpenedFromExpediente(true);
        }
        const openHistory = Boolean((location.state as any)?.openHistory);
        const loadHistoryAndOpen = async () => {
            setLoadingHistory(true);
            try {
                const authHeaders = await createAuthHeaders();
                const res = await fetch(`${apiClient.baseUrl}/api/v1/dashboard/legal-research/documents`, {
                    headers: { ...authHeaders }
                });
                if (!res.ok) throw new Error("Failed to fetch history");
                const data = await res.json();
                const docs = data.documents || [];
                setHistoryItems(docs);
                const match = docs.find((d: HistoryItem) => d.id === historyItemId);
                if (match) {
                    loadResearchHistoryItem(match, !openHistory);
                    if (openHistory) setShowHistory(true);
                    toast.success("Investigación histórica cargada");
                } else if (linkedArtifact) {
                    loadResearchHistoryItem(linkedArtifact, !openHistory);
                    if (openHistory) setShowHistory(true);
                    toast.success("Investigación vinculada cargada");
                } else {
                    toast.error("No se encontró el registro en historial");
                }
            } catch (error) {
                console.error("Error loading linked history:", error);
                if (linkedArtifact) {
                    loadResearchHistoryItem(linkedArtifact, !openHistory);
                    if (openHistory) setShowHistory(true);
                    toast.success("Investigación vinculada cargada");
                } else {
                    toast.error("Error al abrir el historial vinculado");
                }
            } finally {
                setLoadingHistory(false);
                navigate(location.pathname, { replace: true, state: {} });
            }
        };
        loadHistoryAndOpen();
    }, [location.state, apiClient.baseUrl, loadResearchHistoryItem, navigate, location.pathname]);

    const researchRunId = "legal-research-run";
    const currentRun = getRun(researchRunId);
    const loading = currentRun?.status === "loading";
    const isProcessing = loading || currentRun?.status === "streaming";
    const [localResult, setLocalResult] = useState<ResearchResult | null>(null);
    const result = isProcessing
        ? (currentRun?.metadata as (ResearchResult & { search_id?: string }))
        : localResult;

    // Debug state changes
    useEffect(() => {
    }, [currentSearchId, rootSearchId]);

    // Update search IDs when results arrive from backend
    useEffect(() => {
        if (result?.search_id && result.search_id !== currentSearchId) {
            setCurrentSearchId(result.search_id);
            // If we don't have a root ID yet, this FIRST one becomes the root
            if (!rootSearchId) {
                setRootSearchId(result.search_id);
            }
        }
    }, [result?.search_id, currentSearchId, rootSearchId]);

    const handleInvestigate = async (overrideQuery?: string, isNavLink: boolean = false) => {
        const selectedSkill = skills.find(s => s.id === form.skill_id);
        const queryToUse = overrideQuery || form.query || selectedSkill?.name || "";

        if (!queryToUse.trim()) {
            toast.error("Por favor, ingrese un tema de investigación o seleccione un Skill.");
            return;
        }
        setForm(f => ({ ...f, query: queryToUse }));
        setLocalResult(null);
        setMobileWorkspaceTab("analysis");

        // If this is a NavLink (from a completely new start point or history), reset IDs
        if (isNavLink && !overrideQuery) {
            setRootSearchId(null);
            setCurrentSearchId(null);
        }

        // Reset scroll for the analysis panel
        if (analysisPanelRef.current) {
            analysisPanelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // Add to query path if not already the last one
        setQueryPath(prev => {
            const last = prev[prev.length - 1];
            if (last?.query === queryToUse) return prev;
            return [...prev, { query: queryToUse, timestamp: Date.now() }];
        });

        try {
            // Include parent_id in request if this is a subquery
            // The parent_id is always the rootSearchId to maintain a flat hierarchy
            const pid = isNavLink ? null : rootSearchId;

            const payload = {
                ...form,
                query: queryToUse,
                parent_id: pid,
                skill_id: form.skill_id === "none" ? undefined : form.skill_id
            };

            // Transition to result view immediately so the user sees the progress steps
            setStep(3);

            // Track usage for dashboard
            if (user) {
                UsageTracker.trackLegalResearch(user.id, "Investigación iniciada", form.jurisdiction, undefined);
            }

            const agentData = getAgentBySlug("legal-research");
            await startResearchRun(researchRunId, payload, apiClient, user?.id || null, agentData?.system_prompt);
        } catch (e) {
            console.error("Failed to run legal research:", e);
        }
    };

    useEffect(() => {
        if (currentRun?.status === "completed" && currentRun.metadata) {
            const finalRes = currentRun.metadata as ResearchResult;
            setLocalResult(finalRes);

            const query = (currentRun.metadata as any).research_topic || form.query;

            // Update chat history with assistant response
            setChatMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.role === "assistant") {
                    // Update current assistant message
                    const updated = [...prev];
                    updated[updated.length - 1] = {
                        ...lastMsg,
                        content: finalRes.summary || finalRes.executive_summary || "Análisis completado."
                    };
                    return updated;
                } else {
                    return [...prev, {
                        role: "assistant",
                        content: finalRes.summary || finalRes.executive_summary || "Análisis completado.",
                        timestamp: new Date().toISOString()
                    }];
                }
            });

            setQueryPath(prev => {
                const idx = prev.findIndex(p => p.query === query && !p.result);
                if (idx === -1) return prev;
                const newPath = [...prev];
                newPath[idx] = { ...newPath[idx], result: finalRes };
                return newPath;
            });
        }
    }, [currentRun?.status, currentRun?.metadata]);

    const handleClearChat = () => {
        setChatMessages([]);
        toast.success("Chat limpiado");
    };

    const handleSendMessage = async () => {
        if (!chatInput.trim() || isProcessing) return;

        const message = chatInput.trim();
        setChatInput("");

        // Add user message to chat
        setChatMessages(prev => [...prev, {
            role: "user",
            content: message,
            timestamp: new Date().toISOString()
        }]);

        // Add placeholder assistant message
        setChatMessages(prev => [...prev, {
            role: "assistant",
            content: "Analizando...",
            timestamp: new Date().toISOString()
        }]);

        await handleInvestigate(message);
    };

    const handleNavigatePath = (item: { query: string; result?: ResearchResult }) => {
        if (item.result) {
            setForm(f => ({ ...f, query: item.query }));
            setLocalResult(item.result);
            if (item.result.search_id) setCurrentSearchId(item.result.search_id);
            setStep(3);
            if (analysisPanelRef.current) {
                analysisPanelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            handleInvestigate(item.query);
        }
    };

    const handleNewSearch = () => {
        setForm(f => ({ ...f, query: "" }));
        setQueryPath([]);
        setLocalResult(null);
        setCurrentSearchId(null);
        setRootSearchId(null);
        setStep(1);
    };

    const handleDraftFromResult = (item: any) => {
        const title = item.title || "Documento desde Investigación";
        const summary = item.ratio_decidendi || item.summary || "";
        const explanation = item.relevance_explanation || "";
        const docType = item.suggested_document_type || "Documento Jurídico";

        const draftingPrompt = `Generar un documento de tipo "${docType}" basado en el siguiente precedente jurídico:
        
SENTENCIA/NORMA: ${title}
RESUMEN/RATIO: ${summary}
POR QUÉ ES RELEVANTE: ${explanation}

El documento debe aplicar los principios de este precedente al contexto de mi caso actual.`;

        // Build key points from the research data
        const keyPointsList: string[] = [];
        if (item.ratio_decidendi) {
            keyPointsList.push(`• Ratio decidendi: ${item.ratio_decidendi}`);
        }
        if (item.extract) {
            keyPointsList.push(`• Extracto relevante: ${item.extract}`);
        }
        if (item.relevance_explanation) {
            keyPointsList.push(`• Relevancia: ${item.relevance_explanation}`);
        }
        // Add legal concepts from the overall result if available
        if (result?.legal_concepts?.length) {
            const formattedConcepts = result.legal_concepts.map(concept =>
                typeof concept === 'string' ? concept : concept.term
            ).join(", ");
            keyPointsList.push(`• Conceptos jurídicos: ${formattedConcepts}`);
        }
        // Add síntesis jurídica key elements
        if (result?.sintesis_juridica) {
            const sj = result.sintesis_juridica;
            if (sj.problema_juridico) keyPointsList.push(`• Problema jurídico: ${sj.problema_juridico}`);
            if (sj.regla_juridica) keyPointsList.push(`• Regla jurídica: ${sj.regla_juridica}`);
            if (sj.conclusion) keyPointsList.push(`• Conclusión: ${sj.conclusion}`);
        }
        const keyPoints = keyPointsList.join("\n");

        toast.info(`Iniciando redacción de ${docType}...`);

        navigate("/dashboard/agents/document-drafting-v2", {
            state: {
                prefill: {
                    documentType: docType,
                    description: draftingPrompt,
                    keyPoints: keyPoints,
                    category: docType.toLowerCase().includes("demanda") ? "Judicial" : "Comercial"
                }
            }
        });
    };

    const handleDownloadMemo = async () => {
        if (!result) {
            toast.error("No hay resultados de investigación para generar un memorando.");
            return;
        }

        const toastId = toast.loading("Generando Memorando Jurídico...");
        try {
            const authHeaders = await createAuthHeaders();
            const response = await fetch(`${apiClient.baseUrl}/api/v1/dashboard/legal-research/generate-memorandum`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify({
                    result: result,
                    topic: form.query,
                    format: 'docx', // Default format
                    filename: `Memorando_${form.query.replace(/[^a-z0-9]/gi, '_').substring(0, 50)}`
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || "Error al generar el memorando");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Memorando_${form.query.replace(/[^a-z0-9]/gi, '_').substring(0, 50)}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast.success("Memorando generado y descargado correctamente", { id: toastId });
        } catch (error: any) {
            console.error("Error generating memo:", error);
            toast.error(error.message || "Error al generar el memorando", { id: toastId });
        }
    };

    const getAuthorityColor = (level?: string) => {
        const l = (level || "").toLowerCase();
        if (l.includes("alta") || l.includes("vinculante") || l.includes("unificada")) return "bg-green-100 text-green-700 border-green-200";
        if (l.includes("aislada") || l.includes("minoritaria")) return "bg-red-100 text-red-700 border-red-200";
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
    };

    const sortedCases = useMemo(() => {
        if (!result?.cases) return [];
        return [...result.cases].sort((a, b) => parseInt(b.date) - parseInt(a.date));
    }, [result?.cases]);

    return (
        <div className={`h-full flex flex-col font-sans transition-colors duration-300 ${isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>

            {/* History Sheet */}
            <Sheet open={showHistory} onOpenChange={setShowHistory}>
                <SheetContent side="right" className={`w-[400px] sm:w-[540px] overflow-y-auto ${isDarkMode ? 'bg-slate-900 border-l-slate-800' : 'bg-slate-50'}`}>
                    <SheetHeader>
                        <SheetTitle className={isDarkMode ? 'text-white' : 'text-slate-900'}>Historial de Investigaciones</SheetTitle>
                        <SheetDescription className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                            Tus investigaciones y análisis jurídicos anteriores.
                        </SheetDescription>
                        {openedFromExpediente && (
                            <div className={`mt-3 inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold ${isDarkMode ? 'border-teal-700 bg-teal-900/30 text-teal-200' : 'border-teal-200 bg-teal-50 text-teal-700'}`}>
                                <History className="h-3.5 w-3.5" />
                                Registro abierto desde Actividad IA del expediente
                            </div>
                        )}
                    </SheetHeader>

                    <div className="mt-6 flex flex-col gap-4 pb-12">
                        {loadingHistory ? (
                            <div className="flex items-center justify-center p-8">
                                <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
                            </div>
                        ) : historyItems.length === 0 ? (
                            <div className="text-center p-8 text-slate-500">
                                No tienes investigaciones anteriores registradas en tu historial.
                            </div>
                        ) : (
                            historyItems.map((item, i) => (
                                <div key={item.id || i} className={`p-4 rounded-xl border cursor-pointer shadow-sm hover:border-teal-500 hover:shadow-md transition-all ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} onClick={() => loadResearchHistoryItem(item)}>
                                    <div className="flex justify-between items-start mb-1">
                                        <h4 className={`font-bold text-sm line-clamp-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.query || item.title || item.metadata?.query || item.metadata?.research_topic || "Investigación"}</h4>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-slate-400 hover:text-red-500 -mt-1 -mr-1"
                                            onClick={(e) => handleDeleteHistory(e, item.id)}
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </div>
                                    <p className={`text-xs line-clamp-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{item.metadata?.summary || item.metadata?.executive_summary || item.content || "Sin resumen procesado"}</p>
                                    <div className={`mt-3 flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                        <Clock className="h-3 w-3" />
                                        {new Date(item.created_at || Date.now()).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            {/* ── TOP NAV ── */}
            <div className={`flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-teal-600 flex items-center justify-center text-white shadow-md flex-shrink-0">
                        <Scale className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <div>
                        <h1 className="text-base sm:text-xl font-bold tracking-tight">Investigación Legal V2</h1>
                        <p className={`text-[10px] sm:text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Workspace de Inteligencia Jurisprudencial</p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-4">
                    {step === 3 && !loading && !isMobile && (
                        <div className="flex items-center gap-2 border-r pr-4 border-slate-200 dark:border-slate-800 mr-2">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant={activeTab === "chat" ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setActiveTab(activeTab === "chat" ? "insights" : "chat")}
                                            className={cn("h-8 w-8 p-0", activeTab === "chat" && "bg-indigo-600 hover:bg-indigo-700")}
                                        >
                                            <MessageSquare className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{activeTab === "chat" ? "Mostrar Insights" : "Chat con Asistente"}</p>
                                    </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant={showSources ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setShowSources(!showSources)}
                                            className={cn("h-8 w-8 p-0", showSources && "bg-teal-600 hover:bg-teal-700")}
                                        >
                                            <FileBox className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{showSources ? "Ocultar Evidencia" : "Mostrar Evidencia"}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setShowHistory(true)} className="flex px-2 sm:px-3 text-xs">
                        <History className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Historial</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setIsDarkMode(!isDarkMode)} className="px-2.5 sm:px-3 text-xs h-8">
                        {isDarkMode ? 'Claro' : 'Oscuro'}
                    </Button>
                </div>
            </div>

            {/* ── MAIN WORKSPACE ── */}
            {step < 3 ? (
                // FORM VIEW
                <div className={`flex-1 flex flex-col items-center justify-center p-3 sm:p-6 ${isDarkMode ? 'bg-slate-900' : 'bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-teal-50 via-slate-50 to-white'}`}>
                    <div className={`w-full max-w-3xl p-4 sm:p-8 rounded-2xl shadow-xl ${isDarkMode ? 'bg-slate-850 border border-slate-800' : 'bg-white border border-slate-100'}`}>
                        <h2 className="text-xl sm:text-2xl font-bold mb-2 flex items-center gap-2">
                            <Search className="text-teal-600 h-5 w-5 sm:h-6 sm:w-6" />
                            ¿Qué problema jurídico deseas investigar?
                        </h2>

                        {form.skill_id && form.skill_id !== "none" && (() => {
                            const selectedSkill = skills.find(s => s.id === form.skill_id);
                            if (!selectedSkill) return null;
                            return (
                                <div className="mb-6 animate-in fade-in slide-in-from-top-2 duration-500">
                                    <div className={`p-5 rounded-2xl border-2 border-indigo-500/30 ${isDarkMode ? 'bg-indigo-900/10' : 'bg-indigo-50/50'} relative overflow-hidden group shadow-sm`}>
                                        <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                                            <Sparkles className="h-14 w-14 text-indigo-500" />
                                        </div>
                                        <div className="flex items-center gap-4 mb-3 relative z-10">
                                            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
                                                <Bot className="h-5 w-5" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-0.5">Skill de Investigación Activo</h3>
                                                <p className="text-base font-black text-slate-800 dark:text-slate-100 leading-none">{selectedSkill.name}</p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                onClick={() => setForm(f => ({ ...f, skill_id: "none" }))}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <div className="relative z-10 pl-14">
                                            <p className={`text-xs leading-relaxed font-bold mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                {selectedSkill.description?.replace("[LEGAL_RESEARCH]", "").trim() || "Este skill aplicará reglas y metodologías personalizadas a tu investigación."}
                                            </p>

                                            {/* Granular Rules and Themes */}
                                            {loadingSkillDetails ? (
                                                <div className="flex items-center gap-2 py-4 px-3 bg-white/50 dark:bg-slate-800/50 rounded-xl border border-dashed border-indigo-200 dark:border-indigo-800">
                                                    <Loader2 className="h-3 w-3 animate-spin text-indigo-500" />
                                                    <span className="text-[10px] font-bold text-indigo-400 uppercase">Cargando parámetros especializados...</span>
                                                </div>
                                            ) : activeSkillDetails?.topics ? (
                                                <div className="space-y-2 mt-4">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <BookOpen className="h-3 w-3 text-indigo-500" />
                                                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Temas y Reglas de Cumplimiento</span>
                                                    </div>
                                                    <div className="grid grid-cols-1 gap-2">
                                                        {activeSkillDetails.topics.map((topic: any, idx: number) => (
                                                            <div key={idx} className={`p-3 rounded-xl border ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'} transition-all`}>
                                                                <div
                                                                    className="flex items-center justify-between cursor-pointer group/topic"
                                                                    onClick={() => setExpandedTopicId(expandedTopicId === `topic-${idx}` ? null : `topic-${idx}`)}
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                                                        <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 group-hover/topic:text-indigo-600 transition-colors">{topic.name || topic.topic_name}</span>
                                                                    </div>
                                                                    {expandedTopicId === `topic-${idx}` ? <ChevronUp className="h-3 w-3 text-slate-400" /> : <ChevronDown className="h-3 w-3 text-slate-400" />}
                                                                </div>

                                                                {expandedTopicId === `topic-${idx}` && (
                                                                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 space-y-2 animate-in fade-in slide-in-from-top-1 duration-300">
                                                                        <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed italic mb-2">
                                                                            {topic.description || "Sin descripción adicional."}
                                                                        </p>
                                                                        {topic.rules?.map((rule: any, ridx: number) => (
                                                                            <div key={ridx} className="flex items-start gap-2 pl-2">
                                                                                <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                                                                <span className="text-[10px] text-slate-600 dark:text-slate-400 font-medium">{rule.text || rule.rule_name || rule}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mt-2 flex items-center gap-2">
                                                    <span className="flex h-1.5 w-1.5 rounded-full bg-indigo-500 shadow-[0_0_5px_rgba(99,102,241,0.8)]" />
                                                    <span className="text-[9px] font-black uppercase tracking-tighter text-indigo-500/70">Configuración básica lista</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        <Textarea
                            value={form.query}
                            onChange={(e) => setForm(f => ({ ...f, query: e.target.value }))}
                            placeholder="Ej: Requisitos para la acción de tutela contra providencias judiciales por defecto fáctico..."
                            className={`min-h-[140px] text-lg p-4 mb-6 rounded-xl resize-none ${isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-slate-50 border-slate-200'}`}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Jurisdicción</label>
                                <Select value={form.jurisdiction} onValueChange={(v) => setForm(f => ({ ...f, jurisdiction: v }))}>
                                    <SelectTrigger className={`h-12 rounded-xl ${isDarkMode ? 'bg-slate-900 border-slate-700' : ''}`}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {JURISDICTIONS.map(j => (
                                            <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Sugerencias (Conceptos Jurídicos)</label>
                                <div className="flex flex-wrap gap-2">
                                    {["Silencio administrativo", "Derecho a la salud", "Estabilidad laboral reforzada"].map(tag => (
                                        <span key={tag} onClick={() => setForm(f => ({ ...f, query: tag }))} className={`text-xs px-3 py-1.5 rounded-full cursor-pointer transition-colors ${isDarkMode ? 'bg-slate-800 hover:bg-teal-900 text-slate-300' : 'bg-slate-100 hover:bg-teal-100 text-slate-600'}`}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block flex items-center gap-2">
                                    Relacionar con Expediente Vivo <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm normal-case font-medium">Opcional</span>
                                </label>
                                <Select value={form.case_id} onValueChange={(v) => setForm(f => ({ ...f, case_id: v === "none" ? undefined : v }))}>
                                    <SelectTrigger className={`h-12 rounded-xl border-dashed border-teal-200 bg-teal-50/50 hover:bg-teal-50 transition-colors ${isDarkMode ? 'bg-teal-900/10 border-teal-800' : ''}`}>
                                        <SelectValue placeholder="Seleccionar un expediente..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none" className="italic text-slate-500">Sin relacionar</SelectItem>
                                        {(processes || []).map(p => (
                                            <SelectItem key={p.id} value={p.id!}>
                                                {p.case_number ? `${p.case_number} - ` : ''}{p.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block flex items-center gap-2">
                                    Aplicar Skill de Investigación <span className="text-[10px] bg-indigo-100 text-indigo-500 px-1.5 py-0.5 rounded-sm normal-case font-medium">Opcional</span>
                                </label>
                                <Select value={form.skill_id} onValueChange={(v) => setForm(f => ({ ...f, skill_id: v }))}>
                                    <SelectTrigger className={`h-12 rounded-xl border-dashed border-indigo-200 bg-indigo-50/50 hover:bg-indigo-50 transition-colors ${isDarkMode ? 'bg-indigo-900/10 border-indigo-800' : ''}`}>
                                        <SelectValue placeholder="Seleccionar un Skill..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none" className="italic text-slate-500">Sin Skill específico</SelectItem>
                                        {skills.map(skill => (
                                            <SelectItem key={skill.id} value={skill.id}>
                                                {skill.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex justify-end border-t border-slate-100 dark:border-slate-800 pt-6">
                            <Button
                                onClick={() => handleInvestigate()}
                                disabled={(!form.query.trim() && (!form.skill_id || form.skill_id === "none")) || (loading && !currentRun?.traceSteps?.length)}
                                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl px-8 h-12 text-base font-bold shadow-lg shadow-teal-500/30"
                            >
                                {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Sparkles className="h-5 w-5 mr-2" />}
                                Investigar
                            </Button>

                            {loading && (
                                <Button
                                    variant="outline"
                                    onClick={() => cancelRun(researchRunId)}
                                    className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl px-6 h-12 font-bold text-sm"
                                >
                                    <X className="h-4 w-4 mr-2" /> Cancelar
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                // 3-PANEL WORKSPACE VIEW
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
                    {/* Mobile Segmented Bar */}
                    {isMobile && (
                        <div className={`flex items-center justify-between p-2 border-b shrink-0 z-20 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
                            <div className="flex items-center gap-1 w-full bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                                <button
                                    type="button"
                                    onClick={() => setMobileWorkspaceTab("analysis")}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-bold transition-all ${
                                        mobileWorkspaceTab === "analysis"
                                            ? "bg-white dark:bg-slate-700 text-teal-600 dark:text-teal-400 shadow-sm"
                                            : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                                    }`}
                                >
                                    <Sparkles className="h-3.5 w-3.5" />
                                    <span>Síntesis</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMobileWorkspaceTab("sources")}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-bold transition-all ${
                                        mobileWorkspaceTab === "sources"
                                            ? "bg-white dark:bg-slate-700 text-teal-600 dark:text-teal-400 shadow-sm"
                                            : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                                    }`}
                                >
                                    <FileBox className="h-3.5 w-3.5" />
                                    <span>Evidencia</span>
                                    {((sortedCases?.length || 0) + (result?.legislation?.length || 0)) > 0 && (
                                        <span className="text-[10px] px-1.5 py-0.2 bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded-full font-black">
                                            {(sortedCases?.length || 0) + (result?.legislation?.length || 0)}
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMobileWorkspaceTab("chat");
                                        setActiveTab("chat");
                                    }}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-bold transition-all ${
                                        mobileWorkspaceTab === "chat"
                                            ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                                            : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                                    }`}
                                >
                                    <MessageSquare className="h-3.5 w-3.5" />
                                    <span>Chat</span>
                                    {chatMessages.length > 0 && (
                                        <span className="text-[10px] px-1.5 py-0.2 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-full font-black">
                                            {chatMessages.length}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    <ResizablePanelGroup direction={isMobile ? "vertical" : "horizontal"} className="flex-1 h-full">
                        {/* LEFT PANEL: SOURCES */}
                        {((!isMobile && showSources) || (isMobile && mobileWorkspaceTab === "sources")) && (
                            <>
                                <ResizablePanel defaultSize={isMobile ? 100 : 22} minSize={isMobile ? 100 : 15} maxSize={isMobile ? 100 : 35}>
                                    <div className={`flex flex-col border-r h-full overflow-y-auto ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50/50 border-slate-200'}`}>
                                        <div className={`p-4 border-b sticky top-0 z-10 flex items-center justify-between ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50'}`}>
                                            <h3 className="text-sm font-bold flex items-center gap-2"><FileBox className="h-4 w-4 text-teal-600" /> EVIDENCIA</h3>
                                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleNewSearch} title="Nueva búsqueda"><Search className="h-3 w-3" /></Button>
                                        </div>

                                        <div className="p-3 space-y-4">
                                            {isProcessing ? (
                                                <div className="flex flex-col items-center justify-center p-8 space-y-4">
                                                    <Loader2 className="h-8 w-8 text-teal-500 animate-spin" />
                                                    <p className="text-xs text-slate-500 font-medium animate-pulse text-center">Buscando fuentes...</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="space-y-3">
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-1">Jurisprudencia Clave</h4>
                                                        {sortedCases.map((c, i) => (
                                                            <div key={i}
                                                                className={`p-3 rounded-xl border transition-all ${c.url ? 'cursor-pointer hover:border-teal-400 shadow-sm' : 'opacity-80'} ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                                                                onClick={() => c.url && openDocument(c.url, c.title, c.extract || c.ratio_decidendi)}
                                                            >
                                                                <div className="flex justify-between items-start mb-1.5">
                                                                    <span className={`text-xs font-bold ${c.url ? 'text-teal-700 dark:text-teal-400 underline underline-offset-2' : 'text-slate-600 dark:text-slate-400'}`}>{c.title}</span>
                                                                    <span className="text-[10px] text-slate-400">{c.date}</span>
                                                                </div>
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <div className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${getAuthorityColor(c.authority_level)}`}>
                                                                        {c.authority_level || "Precedente"}
                                                                    </div>
                                                                    {c.relevance_score && (
                                                                        <span className="text-[9px] font-black text-indigo-500 leading-none">{c.relevance_score}% MATCH</span>
                                                                    )}
                                                                </div>
                                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">{c.summary}</p>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="space-y-3 pt-2">
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-1">Marco Legal</h4>
                                                        {result?.legislation?.map((l, i) => (
                                                            <div key={i}
                                                                className={`p-3 rounded-xl border transition-all ${l.url ? 'cursor-pointer hover:border-teal-400 shadow-sm' : 'opacity-80'} ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                                                                onClick={() => l.url && openDocument(l.url, l.title)}
                                                            >
                                                                <span className={`text-xs font-bold block mb-1 ${l.url ? 'text-blue-700 dark:text-blue-400 underline' : 'text-slate-600 dark:text-slate-400'}`}>{l.title}</span>
                                                                {l.relevance_score && (
                                                                    <div className="flex items-center gap-2 mb-1.5 font-bold">
                                                                        <div className="h-0.5 w-6 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex-shrink-0">
                                                                            <div className="h-full bg-blue-500" style={{ width: `${l.relevance_score}%` }}></div>
                                                                        </div>
                                                                        <span className="text-[9px] text-blue-500 leading-none">{l.relevance_score}%</span>
                                                                    </div>
                                                                )}
                                                                <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-2">{l.summary}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </ResizablePanel>
                                {!isMobile && <ResizableHandle withHandle />}
                            </>
                        )}

                        {/* CENTER PANEL: ANALYSIS */}
                        {(!isMobile || mobileWorkspaceTab === "analysis") && (
                            <ResizablePanel defaultSize={isMobile ? 100 : 56}>
                            <div
                                ref={analysisPanelRef}
                                className={`flex flex-col h-full overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}
                            >
                                {isProcessing && !result?.sintesis_juridica ? (
                                    <div className="flex-1 flex flex-col items-center justify-center p-8">
                                        <Scale className="h-16 w-16 text-teal-200 animate-pulse mb-6" />
                                        <h3 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">Construyendo Síntesis Jurídica</h3>
                                        <p className="text-slate-500 max-w-md text-center mb-4">Analizando jurisprudencia y elaborando razonamiento...</p>

                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="mb-8 border-red-100 text-red-500 hover:bg-red-50 hover:text-red-600 font-bold text-xs rounded-xl h-10 px-6"
                                            onClick={() => cancelRun(researchRunId)}
                                        >
                                            <X className="h-4 w-4 mr-2" /> CANCELAR INVESTIGACIÓN
                                        </Button>

                                        {currentRun?.traceSteps && currentRun.traceSteps.length > 0 && (
                                            <div className={`w-full max-w-md p-5 rounded-2xl border ${isDarkMode ? 'bg-slate-800/80 border-slate-700 shadow-inner shadow-slate-900/50' : 'bg-slate-50/80 border-slate-200 shadow-inner shadow-slate-200/50'} backdrop-blur-sm`}>
                                                <div className="flex items-center gap-2 mb-4 border-b border-slate-200 dark:border-slate-700 pb-3">
                                                    <Loader2 className="h-4 w-4 animate-spin text-teal-600 dark:text-teal-400" />
                                                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Progreso del Pipeline</h4>
                                                </div>
                                                <div className="space-y-4 max-h-[250px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600">
                                                    {currentRun.traceSteps.map((step, i) => (
                                                        <div key={i} className={`flex items-start gap-3 transition-opacity duration-300 ${step.status === 'done' ? 'opacity-50' : 'opacity-100'}`}>
                                                            <span className="text-lg bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0">{step.emoji}</span>
                                                            <div className="flex-1 pt-1">
                                                                <p className={`text-sm font-semibold leading-tight ${step.status === 'running' ? 'text-teal-700 dark:text-teal-400' : isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                                    {step.message}
                                                                </p>
                                                                {step.detail && step.status === 'running' && (
                                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">{step.detail}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : result?.status === 'rejected' ? (
                                    <div className="flex-1 flex flex-col items-center justify-center p-8">
                                        <ShieldAlert className="h-16 w-16 text-red-500 mb-4" />
                                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Consulta Fuera de Alcance</h3>
                                        <p className="text-slate-500 text-center max-w-lg">{result.guard_rail_details?.reason || result.message}</p>
                                        <Button onClick={handleNewSearch} className="mt-6">Volver a intentar</Button>
                                    </div>
                                ) : (
                                    <div className="p-8 max-w-4xl mx-auto w-full space-y-8 pb-32">
                                        {/* Query Header */}
                                        <div>
                                            {form.skill_id && form.skill_id !== "none" && (
                                                <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border text-[10px] font-black uppercase tracking-tighter mb-4 ${isDarkMode ? 'bg-indigo-900/30 border-indigo-800 text-indigo-300' : 'bg-indigo-50 border-indigo-100 text-indigo-700'}`}>
                                                    <Sparkles className="h-3 w-3" />
                                                    Analizado con Skill: {skills.find(s => s.id === form.skill_id)?.name || "Especializado"}
                                                </div>
                                            )}
                                            <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-4 leading-tight">{form.query}</h2>
                                            <div className="flex flex-wrap gap-2">
                                                {result?.legal_concepts?.map((concept, idx) => {
                                                    const conceptStr = typeof concept === 'string' ? concept : concept.term;
                                                    const conceptDef = typeof concept === 'string' ? null : concept.definition;
                                                    return (
                                                        <TooltipProvider key={idx}>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <span
                                                                        onClick={() => handleInvestigate(conceptStr)}
                                                                        className={`text-xs px-3 py-1 font-medium rounded-md cursor-pointer border hover:border-teal-500 transition-colors ${isDarkMode ? 'bg-slate-800 border-slate-700 text-teal-400' : 'bg-teal-50 border-teal-100 text-teal-700'}`}
                                                                    >
                                                                        {conceptStr}
                                                                    </span>
                                                                </TooltipTrigger>
                                                                {conceptDef && (
                                                                    <TooltipContent className="max-w-[280px] p-3 text-xs leading-relaxed bg-slate-900 border border-slate-800 text-white rounded-xl shadow-xl">
                                                                        <p>{conceptDef}</p>
                                                                    </TooltipContent>
                                                                )}
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Query Refinement Section */}
                                        <div className={`p-5 rounded-2xl border-2 border-dashed max-w-full overflow-hidden ${isDarkMode ? 'bg-indigo-900/10 border-indigo-500/20' : 'bg-indigo-50/30 border-indigo-100'}`}>
                                            <div className="flex items-center gap-2 mb-4 text-indigo-600 dark:text-indigo-400">
                                                <Lightbulb className="h-4 w-4" />
                                                <h3 className="text-xs font-bold uppercase tracking-widest">Refinar investigación</h3>
                                            </div>
                                            <div className="flex flex-wrap gap-2 w-full">
                                                {isProcessing && (!result?.suggested_questions || result.suggested_questions.length === 0) ? (
                                                    <div className="flex items-center gap-2 px-4 py-2">
                                                        <Loader2 className="h-3 w-3 animate-spin text-indigo-500" />
                                                        <span className="text-[11px] text-indigo-400 font-medium">Generando sugerencias de investigación...</span>
                                                    </div>
                                                ) : (result?.suggested_questions && result.suggested_questions.length > 0) ? (
                                                    result.suggested_questions.map((suggestion: string) => (
                                                        <Button
                                                            key={suggestion}
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleInvestigate(suggestion)}
                                                            className={`h-auto py-2 px-4 text-[11px] font-bold rounded-xl transition-all hover:scale-105 active:scale-95 whitespace-normal text-left break-words max-w-full ${isDarkMode ? 'bg-slate-800 border-indigo-500/30 text-indigo-300 hover:bg-slate-700' : 'bg-white border-indigo-100 text-indigo-700 hover:bg-indigo-50 shadow-sm'}`}
                                                        >
                                                            {suggestion}
                                                        </Button>
                                                    ))
                                                ) : (
                                                    <p className={`text-xs italic ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        No hay preguntas de refinamiento sugeridas para este tema.
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Structured Síntesis Jurídica */}
                                        <div className="space-y-6">
                                            <div className="flex items-center justify-between border-b pb-2 dark:border-slate-800">
                                                <h3 className="text-lg font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-teal-600" /> SÍNTESIS JURÍDICA</h3>
                                                <Button size="sm" variant="outline" onClick={handleDownloadMemo} className={`h-8 text-xs font-bold gap-1.5 ${isDarkMode ? 'border-slate-700 hover:bg-slate-800' : ''}`}>
                                                    <FileText className="h-3.5 w-3.5" /> Memorando
                                                </Button>
                                            </div>

                                            {result?.sintesis_juridica ? (
                                                <div className="grid grid-cols-1 gap-4">
                                                    <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50/50 border-slate-100'}`}>
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Problema Jurídico</h4>
                                                        <p className="text-sm font-medium leading-relaxed">{result.sintesis_juridica.problema_juridico || "No identificado"}</p>
                                                    </div>
                                                    <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-teal-900/10 border-teal-800/50' : 'bg-teal-50/50 border-teal-100'}`}>
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-teal-600 dark:text-teal-400 mb-2">Regla Jurídica General</h4>
                                                        <p className="text-sm font-bold leading-relaxed">{result.sintesis_juridica.regla_juridica}</p>
                                                    </div>
                                                    <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-amber-900/10 border-amber-800/50' : 'bg-amber-50/50 border-amber-100'}`}>
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-2">Excepciones</h4>
                                                        <p className="text-sm leading-relaxed">{result.sintesis_juridica.excepcion}</p>
                                                    </div>
                                                    <div className={`p-5 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-indigo-500 mb-2">Conclusión</h4>
                                                        <div className="prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: marked.parse(result.sintesis_juridica.conclusion, { async: false }) as string }} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                                        {isProcessing ? (
                                                            <div className="flex items-center gap-3">
                                                                <Loader2 className="h-4 w-4 animate-spin text-teal-500" />
                                                                <span className="text-slate-500">Generando síntesis jurídica...</span>
                                                            </div>
                                                        ) : (
                                                            <div dangerouslySetInnerHTML={{ __html: marked.parse(result?.summary || result?.executive_summary || "No se pudo recopilar una síntesis jurídica para esta consulta.", { async: false }) as string }} />
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* ⚖️ Jurisprudencia en Tensión Section */}
                                            <div className="space-y-4 pt-4">
                                                <div className="flex items-center gap-2 border-b pb-2 dark:border-slate-800">
                                                    <Scale className="h-4 w-4 text-amber-500" />
                                                    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-600 dark:text-slate-400">Jurisprudencia en Tensión</h3>
                                                </div>

                                                {result?.jurisprudence_trend?.tension_points && result.jurisprudence_trend.tension_points.length > 0 ? (
                                                    <div className="grid grid-cols-1 gap-4">
                                                        {result.jurisprudence_trend.tension_points.map((tp, idx) => (
                                                            <div key={idx} className={`p-5 rounded-2xl border-l-4 border-l-amber-500 border ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-amber-50/20 border-amber-100'}`}>
                                                                <div className="flex items-center gap-3 mb-3">
                                                                    <div className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-black">{tp.case_a}</div>
                                                                    <div className="text-slate-400 text-xs font-bold">VS</div>
                                                                    <div className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-black">{tp.case_b}</div>
                                                                </div>
                                                                <p className="text-sm font-medium leading-relaxed italic text-slate-700 dark:text-slate-300">
                                                                    "{tp.conflict}"
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className={`p-4 rounded-xl border border-dashed text-center ${isDarkMode ? 'bg-slate-800/20 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                        {isProcessing ? (
                                                            <div className="flex items-center justify-center gap-2">
                                                                <Loader2 className="h-3 w-3 animate-spin text-amber-500" />
                                                                <p className="text-xs text-amber-500/70 font-medium italic">Analizando posibles tensiones jurisprudenciales...</p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-slate-400 font-medium">No se detectaron tensiones jurisprudenciales directas en esta materia.</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Ratio Decidendi Cards */}
                                        <div className="space-y-6 pt-6 border-t dark:border-slate-800">
                                            <h3 className="text-lg font-bold flex items-center gap-2"><Gavel className="h-5 w-5 text-teal-600" /> RATIO DECIDENDI Y PRECEDENTES</h3>
                                            <div className="grid grid-cols-1 gap-4">
                                                {sortedCases.map((c, i) => (
                                                    <div key={i} className={`flex flex-col rounded-xl border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                                                        <div className={`flex items-center justify-between px-5 py-3 border-b ${isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-50/80 border-slate-100'}`}>
                                                            <div className="flex items-center gap-3">
                                                                <h4
                                                                    className={`font-bold text-base transition-colors ${c.url ? 'text-teal-700 dark:text-teal-400 cursor-pointer hover:underline' : 'text-slate-600 dark:text-slate-400 cursor-default'}`}
                                                                    onClick={() => c.url && openDocument(c.url, c.title, c.extract || c.ratio_decidendi)}
                                                                >
                                                                    {c.title}
                                                                </h4>
                                                                <span className="text-xs text-slate-400">{c.court} &bull; {c.date}</span>
                                                            </div>
                                                            <div className={`px-2.5 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${getAuthorityColor(c.authority_level)}`}>
                                                                {c.authority_level || "Precedente"}
                                                            </div>
                                                        </div>
                                                        <div className="p-5">
                                                            <h5 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Ratio Decidendi</h5>
                                                            <p className="text-sm font-medium leading-relaxed mb-4">{c.ratio_decidendi || c.summary}</p>

                                                            {c.relevance_explanation && (
                                                                <div className={`mb-4 p-4 rounded-xl border-l-4 border-indigo-500 ${isDarkMode ? 'bg-indigo-900/10 border-slate-700' : 'bg-indigo-50 border-indigo-100'}`}>
                                                                    <div className="flex items-center gap-2 mb-2 text-indigo-600 dark:text-indigo-400">
                                                                        <Sparkles className="h-3 w-3" />
                                                                        <h6 className="text-[10px] font-bold uppercase tracking-widest">¿Por qué es relevante?</h6>
                                                                        <div className="ml-auto flex items-center gap-1.5">
                                                                            <div className="text-[10px] font-black">{c.relevance_score || 80}% MATCH</div>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-300 italic">{c.relevance_explanation}</p>
                                                                </div>
                                                            )}

                                                            <div className="flex items-center justify-between mt-2 pt-4 border-t dark:border-slate-700">
                                                                <div className="flex items-center gap-3">
                                                                    {c.url ? (
                                                                        <Button size="sm" variant="link" onClick={() => openDocument(c.url, c.title, c.extract || c.ratio_decidendi)} className="p-0 h-auto text-[10px] font-bold uppercase tracking-wider text-teal-600 gap-1">
                                                                            Leer Sentencia Completa <ExternalLink className="h-3 w-3" />
                                                                        </Button>
                                                                    ) : (
                                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 italic">
                                                                            <ShieldAlert className="h-3 w-3" /> No disponible
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleDraftFromResult(c)}
                                                                    className="h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold uppercase tracking-wider gap-2 px-4 shadow-sm transition-all active:scale-95"
                                                                >
                                                                    <GitMerge className="h-3.5 w-3.5" />
                                                                    Generar {c.suggested_document_type || "Documento"}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </ResizablePanel>
                        )}

                        {/* RIGHT PANEL: INSIGHTS & CHAT */}
                        {(!isMobile || mobileWorkspaceTab === "chat") && (
                            <>
                                {!isMobile && <ResizableHandle withHandle />}
                                <ResizablePanel defaultSize={isMobile ? 100 : 22} minSize={isMobile ? 100 : 15} maxSize={isMobile ? 100 : 35} className="flex flex-col h-full border-l dark:border-slate-800 bg-white dark:bg-slate-900">
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                                <div className={`flex flex-col shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
                                    <div className="px-4 py-2 border-b flex items-center justify-between">
                                        <TabsList className="bg-transparent gap-4 h-8 p-0">
                                            <TabsTrigger
                                                value="insights"
                                                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-teal-600 data-[state=active]:border-b-2 data-[state=active]:border-teal-600 rounded-none h-8 px-1 text-xs font-black uppercase tracking-tighter"
                                            >
                                                Insights
                                            </TabsTrigger>
                                            {form.skill_id && form.skill_id !== "none" && (
                                                <TabsTrigger
                                                    value="skill"
                                                    className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-indigo-600 data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 rounded-none h-8 px-1 text-xs font-black uppercase tracking-tighter"
                                                >
                                                    Skill
                                                </TabsTrigger>
                                            )}
                                            <TabsTrigger
                                                value="chat"
                                                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-indigo-600 data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 rounded-none h-8 px-1 text-xs font-black uppercase tracking-tighter"
                                            >
                                                Chat
                                            </TabsTrigger>
                                        </TabsList>
                                        <div className="flex items-center gap-2">
                                            {activeTab === 'chat' && chatMessages.length > 0 && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleClearChat}
                                                    className="h-6 text-[9px] font-bold text-red-500 hover:text-red-600 hover:bg-red-50 p-1 px-2 rounded-md transition-colors"
                                                >
                                                    BORRAR CHAT
                                                </Button>
                                            )}
                                            <div className="flex items-center gap-1">
                                                <div className={cn("w-2 h-2 rounded-full animate-pulse", isProcessing ? "bg-amber-500" : "bg-emerald-500")} />
                                                <span className="text-[10px] font-bold text-slate-400 uppercase">{isProcessing ? "Procesando" : "Listo"}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <TabsContent value="insights" className="flex-1 overflow-y-auto min-h-0 m-0 outline-none border-none ring-0 scroll-smooth data-[state=active]:block">
                                    <div className={`flex flex-col w-full h-fit ${isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-slate-50/10 text-slate-900'}`}>
                                        {!loading && result?.status !== 'rejected' && (
                                            <div className="p-5 space-y-8 pb-10">
                                                {/* Confidence Score */}
                                                {(() => {
                                                    const rawScore = result?.confidence_score;
                                                    const confidenceVal = rawScore !== undefined
                                                        ? (rawScore <= 1.0 ? Math.round(rawScore * 100) : Math.round(rawScore))
                                                        : 85;
                                                    return (
                                                        <div>
                                                            <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 block">Confiabilidad del Análisis</h4>
                                                            <div className="flex items-end gap-2 mb-2">
                                                                {isProcessing && !result?.confidence_score ? (
                                                                    <>
                                                                        <span className="text-2xl font-black leading-none tracking-tighter text-amber-500 animate-pulse">Calculando...</span>
                                                                        <span className="text-[10px] text-slate-400 mb-0.5 font-bold uppercase">PROCESANDO</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span className="text-3xl font-black leading-none tracking-tighter">{confidenceVal}%</span>
                                                                        <span className="text-[10px] text-slate-400 mb-0.5 font-bold">PRECISIÓN</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                {isProcessing && !result?.confidence_score ? (
                                                                    <div className="h-full bg-amber-500 rounded-full animate-pulse" style={{ width: '100%' }}></div>
                                                                ) : (
                                                                    <div className="h-full bg-teal-500 rounded-full shadow-[0_0_8px_rgba(20,184,166,0.5)]" style={{ width: `${confidenceVal}%` }}></div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {/* Evolution Timeline */}
                                                <div>
                                                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-1.5 font-mono"><Clock className="h-3 w-3" /> Evolución del Precedente</h4>
                                                    <div className="space-y-0 relative before:absolute before:inset-0 before:ml-2 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-300 dark:before:via-slate-700 before:to-transparent">
                                                        {sortedCases.slice(0, 3).map((c, i) => (
                                                            <div key={i} className="relative flex items-center group py-2 pl-6">
                                                                <div className="absolute left-0 w-4 h-4 rounded-full border-4 border-white dark:border-slate-900 bg-teal-500 z-10 transition-transform group-hover:scale-125 shadow-sm"></div>
                                                                <div className={`w-full p-2.5 rounded-xl border text-[11px] shadow-sm transition-all hover:border-teal-400 ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                                                                    <div className="font-black text-teal-600 mb-0.5 tracking-tight">{c.title}</div>
                                                                    <div className="text-[10px] text-slate-500 leading-tight line-clamp-2 italic">"{c.summary.substring(0, 60)}..."</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Research Path Timeline */}
                                                {queryPath.length > 0 && (
                                                    <div>
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-1.5 font-mono"><Search className="h-3 w-3" /> Hilo de Trabajo</h4>
                                                        <div className="relative space-y-3 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200 dark:before:bg-slate-800">
                                                            {queryPath.map((item, idx) => (
                                                                <div key={idx} className="relative pl-8 group">
                                                                    <div className={cn(
                                                                        "absolute left-0 top-1.5 w-6 h-6 rounded-full border-4 border-white dark:border-slate-800 flex items-center justify-center z-10 transition-all shadow-sm",
                                                                        idx === queryPath.length - 1 ? "bg-indigo-600 scale-110" : "bg-slate-200 dark:bg-slate-700"
                                                                    )}>
                                                                        <div className={cn("w-1 h-1 rounded-full", idx === queryPath.length - 1 ? "bg-white" : "bg-slate-400")} />
                                                                    </div>
                                                                    <div
                                                                        onClick={() => handleNavigatePath(item)}
                                                                        className={cn(
                                                                            "p-3 rounded-2xl border text-[11px] font-bold leading-tight cursor-pointer transition-all hover:translate-x-1",
                                                                            idx === queryPath.length - 1
                                                                                ? "bg-indigo-50 border-indigo-200 text-indigo-900 shadow-sm dark:bg-indigo-900/20 dark:border-indigo-800 dark:text-indigo-200"
                                                                                : (isDarkMode ? "bg-slate-800/40 border-slate-700 text-slate-400" : "bg-white border-slate-200 text-slate-600")
                                                                        )}
                                                                    >
                                                                        {item.query}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Jurisprudence Trend */}
                                                {result?.jurisprudence_trend && (
                                                    <div className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-1.5 font-mono"><GitMerge className="h-3 w-3" /> Tendencia Judicial</h4>

                                                        <div className="space-y-4">
                                                            <div className="space-y-2">
                                                                <div className="flex justify-between text-[10px] font-black uppercase tracking-tighter">
                                                                    <span className="text-teal-600">Dominante (Pacífica)</span>
                                                                    <span>{result.jurisprudence_trend.dominante}%</span>
                                                                </div>
                                                                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-teal-500 rounded-full transition-all duration-1000" style={{ width: `${result.jurisprudence_trend.dominante}%` }}></div>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2">
                                                                <div className="flex justify-between text-[10px] font-black uppercase tracking-tighter">
                                                                    <span className="text-amber-500">Tensión / Excepción</span>
                                                                    <span>{result.jurisprudence_trend.excepcion}%</span>
                                                                </div>
                                                                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-amber-400 rounded-full transition-all duration-1000" style={{ width: `${result.jurisprudence_trend.excepcion}%` }}></div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <p className="mt-4 text-[10px] leading-relaxed font-bold text-slate-500 dark:text-slate-400 border-t pt-4 dark:border-slate-700 italic">
                                                            "{result.jurisprudence_trend.trend_summary || "Interpretación jurídica estable y pacífica."}"
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Follow Up Questions */}
                                                {result?.suggested_questions && result.suggested_questions.length > 0 && (
                                                    <div>
                                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-mono"><Lightbulb className="h-3 w-3 inline mr-1" /> Líneas de Profundización</h4>
                                                        <div className="space-y-2">
                                                            {result.suggested_questions.map((q, i) => (
                                                                <div key={i} onClick={() => handleInvestigate(q)} className={`p-3 rounded-2xl border text-[11px] font-bold leading-tight cursor-pointer transition-all hover:bg-slate-100 dark:hover:bg-slate-800 hover:-translate-y-0.5 border-dashed ${isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`}>
                                                                    {q}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>

                                <TabsContent value="skill" className="flex-1 overflow-y-auto min-h-0 m-0 outline-none border-none ring-0 scroll-smooth data-[state=active]:block">
                                    <div className={`p-5 space-y-6 ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50/10'}`}>
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white shadow-lg">
                                                <Bot className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <h4 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Metodología Aplicada</h4>
                                                <p className="text-sm font-black text-slate-800 dark:text-slate-100">{skills.find(s => s.id === form.skill_id)?.name}</p>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            {loadingSkillDetails ? (
                                                <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
                                                    <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Cargando parámetros...</p>
                                                </div>
                                            ) : activeSkillDetails?.topics ? (
                                                activeSkillDetails.topics.map((topic: any, idx: number) => (
                                                    <div key={idx} className={`p-4 rounded-2xl border transition-all ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                                            <span className="text-[11px] font-black text-slate-800 dark:text-slate-200 uppercase tracking-tight">{topic.name || topic.topic_name}</span>
                                                        </div>
                                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed mb-3 italic">
                                                            {topic.description || "Este tema define reglas específicas para el análisis de jurisprudencia."}
                                                        </p>
                                                        <div className="space-y-2 pl-3 border-l-2 border-indigo-100 dark:border-indigo-900/30">
                                                            {topic.rules?.map((rule: any, ridx: number) => (
                                                                <div key={ridx} className="flex items-start gap-2">
                                                                    <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                                                    <span className="text-[10px] text-slate-600 dark:text-slate-400 font-medium">{rule.text || rule.rule_name || rule}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="flex flex-col items-center justify-center py-12 text-center space-y-3 opacity-50">
                                                    <ShieldAlert className="h-6 w-6 text-slate-300" />
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Sin detalles adicionales</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </TabsContent>

                                <TabsContent value="chat" className="flex-1 flex flex-col min-h-0 m-0 outline-none border-none ring-0">
                                    <div className={`flex-1 overflow-y-auto p-4 space-y-6 scroll-smooth ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50/50'}`}>
                                        {chatMessages.length === 0 ? (
                                            <div className="h-full flex flex-col items-center justify-center text-center p-8 space-y-5">
                                                <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-xl shadow-indigo-500/20 rotate-3">
                                                    <Bot className="h-8 w-8 text-white" />
                                                </div>
                                                <div className="space-y-2">
                                                    <p className="text-lg font-black tracking-tight">Asistente Jurídico</p>
                                                    <p className="text-xs text-slate-500 leading-relaxed max-w-[200px] mx-auto font-medium">
                                                        Consulta dudas específicas sobre los fallos encontrados o solicita un resumen ejecutivo.
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            chatMessages.map((msg, i) => (
                                                <div key={i} className={cn(
                                                    "flex flex-col max-w-[90%] space-y-1.5 animate-in fade-in slide-in-from-bottom-2 duration-300",
                                                    msg.role === "user" ? "ml-auto items-end" : "mr-auto items-start"
                                                )}>
                                                    <div className={cn(
                                                        "px-4 py-3 rounded-2xl text-[11px] leading-relaxed shadow-sm font-medium",
                                                        msg.role === "user"
                                                            ? "bg-indigo-600 text-white rounded-tr-none"
                                                            : isDarkMode
                                                                ? "bg-slate-800 border border-slate-700 text-slate-200 rounded-tl-none"
                                                                : "bg-white border border-slate-100 text-slate-800 rounded-tl-none"
                                                    )}>
                                                        <div className="prose prose-xs dark:prose-invert prose-p:my-1 prose-headings:text-xs" dangerouslySetInnerHTML={{ __html: marked.parse(msg.content, { async: false }) as string }} />
                                                    </div>
                                                    <div className="flex items-center gap-1.5 px-1">
                                                        {msg.role === "assistant" ? <Bot className="h-2.5 w-2.5 text-indigo-500" /> : <User className="h-2.5 w-2.5 text-slate-400" />}
                                                        <span className="text-[9px] font-bold text-slate-400 uppercase">
                                                            {msg.role === "user" ? "Usted" : "Asistente AI"} &bull; {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                        <div ref={chatEndRef} />
                                    </div>

                                    <div className={`p-4 border-t shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
                                        <div className="relative group">
                                            <Textarea
                                                value={chatInput}
                                                onChange={(e) => setChatInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendMessage();
                                                    }
                                                }}
                                                placeholder="Haz una pregunta o solicita un ajuste..."
                                                className={`min-h-[90px] w-full p-4 pr-12 text-xs rounded-2xl shadow-inner resize-none transition-all focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? 'bg-slate-850 border-slate-700 text-white placeholder:text-slate-600' : 'bg-slate-50 border-slate-200 placeholder:text-slate-400'}`}
                                            />
                                            <Button
                                                size="icon"
                                                onClick={handleSendMessage}
                                                disabled={!chatInput.trim() || isProcessing}
                                                className={cn(
                                                    "absolute right-3 bottom-3 h-8 w-8 rounded-xl shadow-lg transition-all transform",
                                                    chatInput.trim() ? "bg-indigo-600 hover:bg-indigo-700 scale-100 rotate-0" : "bg-slate-300 scale-90 -rotate-45"
                                                )}
                                            >
                                                {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                            </Button>
                                        </div>
                                        <p className="text-[9px] font-bold text-slate-400 mt-3 flex items-center justify-between uppercase tracking-tighter">
                                            <span className="flex items-center gap-1"><CornerDownLeft className="h-2.5 w-2.5" /> Enter para enviar</span>
                                            <span>IA Especializada v2.1</span>
                                        </p>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </ResizablePanel>
                        </>
                        )}
                    </ResizablePanelGroup>
                </div>
            )}
        </div>
    );
}
